import React from 'react'

interface UseLazyCategoryRenderProps {
  disabled: boolean
  menu: Menu.MenuData[]
}

interface UseLazyCategoryRender {
  shouldShowCategoryDetails: (categoryId: string) => boolean
  shouldShowCategorySkeleton: (categoryId: string) => boolean
  showCategorySkeleton: (categoryId: string) => void
  showCategoryDetails: (categoryId: string) => void
}

const useLazyCategoryRender = ({
  menu,
  disabled
}: UseLazyCategoryRenderProps): UseLazyCategoryRender => {
  const [showSkeleton, setShowSkeleton] = React.useState({})
  const [showDetails, setShowDetails] = React.useState(() => {
    const firstCategoryId = (menu?.[0] || {}).id
    return disabled
      ? menu?.reduce((acc, { id }) => {
          acc[id] = true
          return acc
        }, {})
      : firstCategoryId
      ? { [firstCategoryId]: true }
      : {}
  })

  const displayedCategories = React.useMemo(() => {
    return menu?.reduce((acc, category, idx) => {
      if (showDetails[category.id] || showSkeleton[category.id]) {
        acc[category.id] = true
      }

      if (showDetails[category.id]) {
        const nextCategory = menu[idx + 1]
        if (nextCategory && !showDetails[nextCategory.id]) {
          acc[nextCategory.id] = true
        }
      }

      return acc
    }, {})
  }, [showSkeleton, showDetails, menu])

  const shouldShowCategorySkeleton = (catId) => displayedCategories[catId]
  const shouldShowCategoryDetails = (catId) => showDetails[catId]

  const showCategorySkeleton = React.useCallback(
    (categoryId) => {
      setShowSkeleton((state) => ({ ...state, [categoryId]: true }))
      const index = menu?.findIndex(({ id }) => id === categoryId)
      if (!index || index <= 1) return
      // NOTE: Show details of 2 previous categories to prevent scroll position to jump unexpectedly in case previous category skeletons is in viewport.
      setShowDetails((state) => ({
        ...state,
        [menu?.[index - 1]?.id]: true,
        [menu?.[index - 2]?.id]: true
      }))
    },
    [menu]
  )

  const showCategoryDetails = React.useCallback((categoryId) => {
    // Fake network request
    setTimeout(() => {
      setShowDetails((state) => ({
        ...state,
        [categoryId]: true
      }))
    }, 150)
  }, [])

  return {
    shouldShowCategoryDetails,
    shouldShowCategorySkeleton,
    showCategorySkeleton,
    showCategoryDetails
  }
}

export default useLazyCategoryRender
